import React from "react";
import { Box, Grid, Typography } from "@mui/material";

import { ReactComponent as LinkedinIcon } from "../../static/svg/linkedin-icon.svg";

export type TeamMember = {
  name: string;
  role: string;
  linkedin: string;
  bio: string;
  image?: string;
  accent: string;
};

export const founder: TeamMember = {
  name: "Blessing Adesiyan",
  role: "Founder & Chief Care Officer",
  linkedin: "https://www.linkedin.com/in/blessingadesiyan/",
  bio: "Leading Caring Africa's mission to make care a recognized pillar of Africa's economic and social development.",
  image:
    "https://mh-resources-production.s3.us-west-1.amazonaws.com/blessingAdesiyan.png",
  accent: "#EDD5B1",
};

export const leadership: TeamMember[] = [
  {
    name: "Taye Oyeleye",
    role: "Co-President & CTO",
    linkedin: "https://www.linkedin.com/in/taye-oyeleye/",
    bio: "A tech enthusiast and keynote speaker, Taye leads the technology behind Caring Africa, building the digital infrastructure that helps care reach more people at scale.",
    image:
      "https://mh-resources-production.s3.us-west-1.amazonaws.com/Taye_Oyeleye_Professional_Headshot.jpg",
    accent: "#C7D8E4",
  },
  {
    name: "Kehinde Oyeleye",
    role: "Co-President & CPO",
    linkedin: "https://www.linkedin.com/in/kehinde-oyeleye/",
    bio: "A keynote speaker with a deep passion for technology, Kehinde shapes the products and experiences that connect families, care workers, and partners across the continent.",
    image:
      "https://mh-resources-production.s3.us-west-1.amazonaws.com/Kehinde_Oyeleye_Professional_Headshot.jpg",
    accent: "#DFE9DB",
  },
];

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((part) => part.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();

const LinkedinLink = ({ member }: { member: TeamMember }) => (
  <Box
    component="a"
    href={member.linkedin}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={`${member.name} on LinkedIn`}
    className="!no-underline"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 1.2,
      color: "#194049",
      borderBottom: "1px solid #194049",
      pb: 0.5,
      transition: "gap 0.32s ease-out, opacity 0.32s ease-out",
      "&:hover": { gap: 2, opacity: 0.8 },
    }}
  >
    <LinkedinIcon className="w-4 h-4" />
    <Typography
      variant="body2"
      fontFamily="Area-Normal-Bold"
      fontSize="12px"
      letterSpacing="0.1em"
      className="uppercase"
    >
      Connect on LinkedIn
    </Typography>
    <span aria-hidden="true">&rarr;</span>
  </Box>
);

const Portrait = ({
  member,
  height,
}: {
  member: TeamMember;
  height: { xs: number; md: number };
}) =>
  member.image ? (
    <Box
      component="img"
      src={member.image}
      alt={member.name}
      draggable={false}
      sx={{
        width: "100%",
        height,
        objectFit: "cover",
        objectPosition: "top",
        display: "block",
        filter: "grayscale(15%)",
        transition: "transform 0.6s ease-out, filter 0.6s ease-out",
      }}
      className="portrait"
    />
  ) : (
    <Box
      role="img"
      aria-label={member.name}
      className="portrait"
      sx={{
        width: "100%",
        height,
        backgroundColor: member.accent,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "transform 0.6s ease-out",
      }}
    >
      {/* concentric rings echo the hero motif */}
      {[340, 240, 140].map((size) => (
        <Box
          key={size}
          sx={{
            position: "absolute",
            width: size,
            height: size,
            borderRadius: "50%",
            border: "1px solid rgba(25, 64, 73, 0.15)",
          }}
        />
      ))}
      <Typography
        variant="h3"
        color="primary"
        fontSize={{ xs: "72px", md: "96px" }}
        letterSpacing="-0.06em"
        lineHeight={1}
        sx={{ position: "relative" }}
      >
        {getInitials(member.name)}
      </Typography>
    </Box>
  );

const SectionLabel = ({ index, label }: { index: string; label: string }) => (
  <Box className="flex items-center gap-3">
    <Typography
      variant="caption"
      color="primary"
      fontSize="12px"
      letterSpacing="0.1em"
      fontFamily="Area-Normal-Bold"
    >
      {index}
    </Typography>
    <Box sx={{ width: 40, height: "1px", backgroundColor: "#194049" }} />
    <Typography
      variant="caption"
      color="primary"
      fontSize="12px"
      letterSpacing="0.1em"
      className="uppercase"
    >
      {label}
    </Typography>
  </Box>
);

export const FounderSpotlight = () => (
  <Grid
    container
    spacing={{ xs: 6, md: 10 }}
    alignItems="center"
    sx={{ "&:hover .portrait": { transform: "scale(1.03)", filter: "none" } }}
  >
    <Grid item xs={12} md={5}>
      <Box sx={{ position: "relative", mr: { xs: 3, md: 0 } }}>
        <Box
          sx={{
            position: "absolute",
            top: 24,
            left: 24,
            right: -24,
            bottom: -24,
            backgroundColor: founder.accent,
          }}
        />
        <Box sx={{ position: "relative", overflow: "hidden" }}>
          <Portrait member={founder} height={{ xs: 380, md: 480 }} />
        </Box>
      </Box>
    </Grid>

    <Grid item xs={12} md={7}>
      <SectionLabel index="01" label="Founder" />

      <Typography
        variant="h3"
        color="primary"
        fontSize={{ xs: "44px", sm: "56px", md: "72px" }}
        lineHeight={{ xs: "100%", md: "95%" }}
        letterSpacing="-0.06em"
        className="pt-6"
      >
        {founder.name}
      </Typography>

      <Typography
        variant="body1"
        color="primary"
        fontFamily="Area-Normal-Bold"
        fontSize={{ xs: "13px", md: "14px" }}
        letterSpacing="0.1em"
        className="uppercase pt-4"
      >
        {founder.role}
      </Typography>

      <Box
        sx={{
          borderLeft: "2px solid #A0BDD2",
          pl: 3,
          my: 5,
          maxWidth: 520,
        }}
      >
        <Typography
          variant="body1"
          color="primary"
          fontSize={{ xs: "15px", md: "16px" }}
          lineHeight="180%"
          letterSpacing="0.03em"
        >
          {founder.bio}
        </Typography>
      </Box>

      <LinkedinLink member={founder} />
    </Grid>
  </Grid>
);

const LeaderCard = ({
  member,
  index,
}: {
  member: TeamMember;
  index: number;
}) => (
  <Box
    sx={{
      backgroundColor: "#FFFFFF",
      height: "100%",
      transition: "transform 0.4s ease-out, box-shadow 0.4s ease-out",
      "&:hover": {
        transform: "translateY(-6px)",
        boxShadow: "0px 18px 32px 0px rgba(25, 64, 73, 0.12)",
      },
      "&:hover .portrait": { transform: "scale(1.04)", filter: "none" },
    }}
  >
    <Box sx={{ overflow: "hidden" }}>
      <Portrait member={member} height={{ xs: 300, md: 360 }} />
    </Box>

    <Box sx={{ p: { xs: 3, md: 4 } }}>
      <SectionLabel
        index={String(index + 2).padStart(2, "0")}
        label="Leadership"
      />
      <Typography
        variant="h3"
        color="primary"
        fontSize={{ xs: "34px", md: "40px" }}
        lineHeight="105%"
        letterSpacing="-0.05em"
        className="pt-5"
      >
        {member.name}
      </Typography>
      <Typography
        variant="body1"
        color="primary"
        fontFamily="Area-Normal-Bold"
        fontSize="13px"
        letterSpacing="0.1em"
        className="uppercase pt-3"
      >
        {member.role}
      </Typography>
      <Box sx={{ borderLeft: "2px solid #A0BDD2", pl: 2.5, my: 4 }}>
        <Typography
          variant="body1"
          color="primary"
          fontSize={{ xs: "14px", md: "15px" }}
          lineHeight="180%"
          letterSpacing="0.03em"
        >
          {member.bio}
        </Typography>
      </Box>
      <LinkedinLink member={member} />
    </Box>
  </Box>
);

export const LeadershipGrid = () => (
  <Grid container spacing={{ xs: 4, md: 5 }}>
    {leadership.map((member, index) => (
      <Grid item xs={12} sm={6} key={member.name}>
        <LeaderCard member={member} index={index} />
      </Grid>
    ))}
  </Grid>
);
